'use client'

import { useCallback, useState } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { ExtraItem } from '@booking/types/bookingConfig'
import type { ExtraQuantities } from '@booking/types/booking'

// Reuse parent list/item layout and TicketType control styles for visual consistency
import listStyles from '../../index.module.scss'
import controlStyles from '../TicketType/index.module.scss'
import styles from './index.module.scss'

import MinusIcon from '../TicketType/minus-icon.svg'
import PlusIcon from '../TicketType/plus-icon.svg'

type ExtrasSectionProps = {
  /** When false, hides the section title (use when the parent already shows it as a page heading). */
  showTitle?: boolean
}

export default function ExtrasSection({ showTitle = true }: ExtrasSectionProps) {
  const t = useTranslations('booking')
  const state = useBookingState()
  const { dispatch } = useBookingActionsCtx()

  const extrasConfig = state.config.extras
  if (!extrasConfig) return null

  const extrasTotal = state.extras.reduce((sum, e) => sum + e.quantity, 0)
  const totalMaxReached =
    typeof extrasConfig.maxExtrasTotal === 'number' && extrasTotal >= extrasConfig.maxExtrasTotal

  const setExtraValue = (category: string, nextQty: number) => {
    const existing = state.extras.find((e) => e.category === category)
    let next: ExtraQuantities

    if (existing) {
      next = state.extras.map((e) => (e.category === category ? { ...e, quantity: nextQty } : e))
    } else {
      next = [...state.extras, { category, quantity: nextQty }]
    }

    dispatch({ type: 'SET_EXTRAS', extras: next })
  }

  return (
    <div className={listStyles.boxComandsWrapper}>
      {showTitle && extrasConfig.titleKey && (
        <h3 className={listStyles.extrasTitle}>{t(extrasConfig.titleKey)}</h3>
      )}

      <ul className={listStyles.ticketTypes}>
        {(extrasConfig.items ?? []).map((item) => {
          const value = state.extras.find((e) => e.category === item.category)?.quantity ?? 0
          return (
            <li key={item.category} className={cn(item.disabled && styles.disabled)}>
              {item.titleKey ? (
                <div className={styles.richLabel}>
                  {item.tagKey && <span className={styles.tag}>{t(item.tagKey)}</span>}
                  <strong className={styles.title}>{t(item.titleKey)}</strong>
                  {item.price && <span className={styles.price}>{item.price} {t('extras.perPerson')}</span>}
                  {item.moreInfoKey && <MoreInfoPopover contentKey={item.moreInfoKey} />}
                </div>
              ) : (
                <span className={listStyles.variant}>{t(item.labelKey)}</span>
              )}
              <ExtraRow item={item} value={value} totalMaxReached={totalMaxReached} onChange={(next) => setExtraValue(item.category, next)} />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------

function MoreInfoPopover({ contentKey }: { contentKey: string }) {
  const t = useTranslations('booking')
  const [open, setOpen] = useState(false)

  const lines = t(contentKey).split('\n').filter(Boolean)

  return (
    <span
      className={styles.moreInfoWrapper}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span className={styles.moreInfoTrigger}>{t('extras.moreInfo')}</span>
      {open && (
        <div className={styles.popover}>
          {lines.map((line, i) => (
            <p key={i} className={styles.popoverLine}>{line}</p>
          ))}
        </div>
      )}
    </span>
  )
}

// ---------------------------------------------------------------------------

type ExtraRowProps = {
  item: ExtraItem
  value: number
  totalMaxReached: boolean
  onChange: (next: number) => void
}

function ExtraRow({ item, value, totalMaxReached, onChange }: ExtraRowProps) {
  const minReached = value <= 0 || Boolean(item.disabled)
  const maxReached =
    Boolean(item.disabled) ||
    (typeof item.max === 'number' ? value >= item.max : false) ||
    totalMaxReached

  const handleRemove = useCallback(() => {
    if (minReached) return
    onChange(value - 1)
  }, [minReached, onChange, value])

  const handleAdd = useCallback(() => {
    if (maxReached) return
    onChange(value + 1)
  }, [maxReached, onChange, value])

  return (
    <span className={cn(controlStyles.controlsHolder)}>
      <button
        type="button"
        className={cn(controlStyles.buttonMath, controlStyles.buttonMinus)}
        disabled={minReached}
        onClick={handleRemove}
        aria-label={`Decrease ${item.category}`}
      >
        <MinusIcon />
      </button>

      <span className={controlStyles.controlValue}>{value}</span>

      <button
        type="button"
        className={cn(controlStyles.buttonMath, controlStyles.buttonPlus)}
        disabled={maxReached}
        onClick={handleAdd}
        aria-label={`Increase ${item.category}`}
      >
        <PlusIcon />
      </button>
    </span>
  )
}
